import React, { useEffect, useMemo, useState } from 'react';
import Pagination from '../../../hooks/Pagination';
import { reviewChecklistServices } from '../../../APIServices/reviewChecklistServices';

const MODULE_OPTIONS = [
  { value: 'protocol_statistical_review', label: 'Protocol Statistical Review' },
  { value: 'csr_review', label: 'CSR Review' },
  { value: 'csr_listing_review', label: 'CSR Listing Review' },
];

const SEVERITY_OPTIONS = ['Critical', 'Major', 'Minor'];

const defaultForm = {
  moduleType: 'protocol_statistical_review',
  category: '',
  checkpoint: '',
  severity: 'Major',
  sortOrder: 0,
  autoRuleKey: '',
};

function ReviewChecklistManagement() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedRows, setDeletedRows] = useState([]);
  const [deletedCount, setDeletedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterModuleType, setFilterModuleType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [deletedPageNumber, setDeletedPageNumber] = useState(1);
  const [deletedItemsPerPage, setDeletedItemsPerPage] = useState(10);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const totalPage = Math.ceil(totalCount / itemsPerPage) || 1;
  const deletedTotalPage = Math.ceil(deletedCount / deletedItemsPerPage) || 1;

  const resetForm = () => {
    setEditingRow(null);
    setForm(defaultForm);
  };

  const loadActive = async () => {
    setLoading(true);
    const data = await reviewChecklistServices.getReviewChecklistData(
      'active',
      filterModuleType,
      searchText,
      pageNumber,
      itemsPerPage
    );
    setRows(data?.data || []);
    setTotalCount(data?.count || 0);
    setLoading(false);
  };

  const loadDeleted = async () => {
    const data = await reviewChecklistServices.getReviewChecklistData(
      'deleted',
      filterModuleType,
      '',
      deletedPageNumber,
      deletedItemsPerPage
    );
    setDeletedRows(data?.data || []);
    setDeletedCount(data?.count || 0);
  };

  useEffect(() => {
    loadActive();
  }, [pageNumber, itemsPerPage, filterModuleType, searchText]);

  useEffect(() => {
    loadDeleted();
  }, [deletedPageNumber, deletedItemsPerPage, filterModuleType]);

  const filteredRows = useMemo(() => rows, [rows]);

  const saveRow = async () => {
    if (!form.moduleType || !form.category || !form.checkpoint) return;

    const payload = {
      moduleType: form.moduleType,
      category: form.category,
      checkpoint: form.checkpoint,
      severity: form.severity,
      sortOrder: Number(form.sortOrder) || 0,
      autoRuleKey: form.autoRuleKey,
    };

    if (editingRow?._id) {
      await reviewChecklistServices.updateReviewChecklist({ id: editingRow._id, ...payload });
    } else {
      await reviewChecklistServices.addNewReviewChecklist(payload);
    }

    resetForm();
    await loadActive();
    await loadDeleted();
  };

  const softDeleteRow = async (row) => {
    const ok = window.confirm(`Delete checklist point: "${row.checkpoint}"?`);
    if (!ok) return;
    await reviewChecklistServices.softDeleteRestoreReviewChecklist({ id: row._id, type: 'delete' });
    await loadActive();
    await loadDeleted();
  };

  const restoreRow = async (row) => {
    const ok = window.confirm(`Restore checklist point: "${row.checkpoint}"?`);
    if (!ok) return;
    await reviewChecklistServices.softDeleteRestoreReviewChecklist({ id: row._id, type: 'restore' });
    await loadActive();
    await loadDeleted();
  };

  const permanentDeleteRow = async (row) => {
    const ok = window.confirm(`Permanently delete checklist point: "${row.checkpoint}"?`);
    if (!ok) return;
    await reviewChecklistServices.deleteReviewChecklistData({ id: row._id });
    await loadDeleted();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Workspace metadata</div>
          <h1 className="page-title">Review Checklist Metadata</h1>
          <p className="page-sub">Manage checklist points for Protocol Statistical Review, CSR Review, and CSR Listing Review modules.</p>
        </div>
      </div>

      <div className="card card-pad mb-3">
        <div className="grid-2" style={{ gap: 12 }}>
          <div className="field">
            <label className="field-label">Module</label>
            <select className="inp" value={form.moduleType} onChange={(e) => setForm((p) => ({ ...p, moduleType: e.target.value }))}>
              {MODULE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Category</label>
            <input className="inp" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="e.g. Study Design" />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Checkpoint</label>
            <textarea className="ta" value={form.checkpoint} onChange={(e) => setForm((p) => ({ ...p, checkpoint: e.target.value }))} style={{ minHeight: 78 }} placeholder="Enter review checkpoint" />
          </div>
          <div className="field">
            <label className="field-label">Severity</label>
            <select className="inp" value={form.severity} onChange={(e) => setForm((p) => ({ ...p, severity: e.target.value }))}>
              {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Sort Order</label>
            <input className="inp" type="number" value={form.sortOrder} onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))} />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Auto Rule Key (Optional)</label>
            <input className="inp" value={form.autoRuleKey} onChange={(e) => setForm((p) => ({ ...p, autoRuleKey: e.target.value }))} placeholder="e.g. EXC-001" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-primary" onClick={saveRow}>
            <i className={`ti ${editingRow ? 'ti-device-floppy' : 'ti-plus'}`} /> {editingRow ? 'Update Point' : 'Add Point'}
          </button>
          {editingRow && (
            <button className="btn btn-ghost" onClick={resetForm}>Cancel Edit</button>
          )}
        </div>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">Checklist Points <span className="count">{totalCount}</span></div>
        <div className="hstack gap-4">
          <select className="inp" style={{ width: 260 }} value={filterModuleType} onChange={(e) => { setFilterModuleType(e.target.value); setPageNumber(1); }}>
            <option value="">All Modules</option>
            {MODULE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <input className="inp" style={{ width: 240 }} value={searchText} onChange={(e) => { setSearchText(e.target.value); setPageNumber(1); }} placeholder="Search category/checkpoint" />
          <button className="btn btn-danger" onClick={() => setShowDeleted((p) => !p)}>
            <i className="ti ti-history" /> {showDeleted ? 'Hide Deleted' : 'Deleted History'}
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 12 }}>Loading checklist metadata...</div>
          </div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            No checklist points found.
          </div>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Category</th>
                  <th>Checkpoint</th>
                  <th style={{ width: 80 }}>Severity</th>
                  <th style={{ width: 80 }}>Order</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row._id}>
                    <td className="xs">{MODULE_OPTIONS.find((m) => m.value === row.moduleType)?.label || row.moduleType}</td>
                    <td>{row.category}</td>
                    <td>{row.checkpoint}</td>
                    <td>{row.severity}</td>
                    <td>{row.sortOrder}</td>
                    <td>
                      <div className="flex gap-2">
                        <button className="icon-btn" title="Edit" style={{ color: 'var(--info)' }} onClick={() => {
                          setEditingRow(row);
                          setForm({
                            moduleType: row.moduleType || defaultForm.moduleType,
                            category: row.category || '',
                            checkpoint: row.checkpoint || '',
                            severity: row.severity || 'Major',
                            sortOrder: row.sortOrder || 0,
                            autoRuleKey: row.autoRuleKey || '',
                          });
                        }}>
                          <i className="ti ti-edit" />
                        </button>
                        <button className="icon-btn" title="Delete" style={{ color: 'var(--danger)' }} onClick={() => softDeleteRow(row)}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalPage={totalPage}
              totalItems={totalCount}
            />
          </>
        )}
      </div>

      {showDeleted && (
        <div className="card mt-3">
          <div className="section-label" style={{ padding: 16, paddingBottom: 0 }}>
            Deleted Checklist Points <span className="count">{deletedCount}</span>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Module</th>
                <th>Category</th>
                <th>Checkpoint</th>
                <th style={{ width: 120 }}>Deleted On</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deletedRows.map((row) => (
                <tr key={row._id}>
                  <td className="xs">{MODULE_OPTIONS.find((m) => m.value === row.moduleType)?.label || row.moduleType}</td>
                  <td>{row.category}</td>
                  <td>{row.checkpoint}</td>
                  <td className="xs">{row.deletedDT ? new Date(row.deletedDT).toLocaleString() : '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-xs" onClick={() => restoreRow(row)}>
                        <i className="ti ti-restore" /> Restore
                      </button>
                      <button className="btn btn-xs" onClick={() => permanentDeleteRow(row)}>
                        <i className="ti ti-trash-x" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {deletedRows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 16 }} className="xs muted">No deleted checklist points</td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination
            itemsPerPage={deletedItemsPerPage}
            setItemsPerPage={setDeletedItemsPerPage}
            pageNumber={deletedPageNumber}
            setPageNumber={setDeletedPageNumber}
            totalPage={deletedTotalPage}
            totalItems={deletedCount}
          />
        </div>
      )}
    </div>
  );
}

export default ReviewChecklistManagement;
